import { db, auth } from '../config/firebase';
import { 
  collection, 
  getDocs, 
  query, 
  where, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  doc, 
  setDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { serviceProvidersData } from '../data/serviceProviders';
import { signInWithEmailAndPassword } from 'firebase/auth';

// Get all service providers
export const getAllProviders = async () => {
  try {
    const providersSnapshot = await getDocs(collection(db, 'providers'));
    if (providersSnapshot.empty) {
      // If no providers exist, initialize them
      await initializeProviders();
      return serviceProvidersData;
    }
    return providersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting providers:', error);
    throw error;
  }
};

// Get providers by category
export const getProvidersByCategory = async (category) => {
  try {
    // Check if user is authenticated
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }

    const q = query(
      collection(db, 'serviceProviders'), 
      where('category', '==', category)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting providers by category:', error);
    throw error;
  }
};

// Populate database (admin only)
export const populateDatabase = async () => {
  try {
    // Check if user is admin
    if (!auth.currentUser || auth.currentUser.email !== 'admin@aroundu.co.za') {
      throw new Error('Only admin can populate the database');
    }

    // Rest of your populate database code...
  } catch (error) {
    console.error('Error populating database:', error);
    throw error;
  }
};

// Admin check utility function
export const isAdmin = (user) => {
  return user && user.email === 'admin@aroundu.co.za';
};

// Use this function wherever you need to check for admin privileges
export const adminOperation = async () => {
  if (!auth.currentUser || auth.currentUser.email !== 'admin@aroundu.co.za') {
    throw new Error('Operation not permitted: Admin access required');
  }
  // ... admin operation code ...
};

export const addProvider = async (providerData) => {
  try {
    // Generate a unique ID
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 8);
    const uniqueId = `provider_${timestamp}_${randomString}`;

    // Prepare the provider data
    const formattedData = {
      id: uniqueId,
      name: providerData.name,
      category: providerData.category,
      phoneNumber: providerData.phoneNumber || '',
      phoneNumber2: providerData.phoneNumber2 || '',
      email: providerData.email || '',
      address: providerData.address || '',
      location: providerData.location?.trim() || '',
      description: providerData.description || '',
      services: providerData.services || [],
      isPriorityListing: providerData.isPriorityListing || false,
      businessHours: providerData.businessHours || {  // Add default business hours structure
        monday: { isOpen: false, open: '09:00', close: '17:00' },
        tuesday: { isOpen: false, open: '09:00', close: '17:00' },
        wednesday: { isOpen: false, open: '09:00', close: '17:00' },
        thursday: { isOpen: false, open: '09:00', close: '17:00' },
        friday: { isOpen: false, open: '09:00', close: '17:00' },
        saturday: { isOpen: false, open: '09:00', close: '17:00' },
        sunday: { isOpen: false, open: '09:00', close: '17:00' }
      },
      reviews: [],
      rating: 0,
      verified: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Add to Firestore
    const docRef = doc(db, 'providers', uniqueId);
    await setDoc(docRef, formattedData);
    
    return uniqueId;
  } catch (error) {
    console.error('Error adding provider:', error);
    throw error;
  }
};

export const updateProvider = async (providerId, providerData) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('User must be authenticated');

    // Check if user is admin
    if (user.email !== 'admin@aroundu.co.za') {
      throw new Error('Insufficient permissions: Admin access required');
    }

    const providerRef = doc(db, 'providers', providerId);
    
    // Include all fields in the update, including isPriorityListing
    await updateDoc(providerRef, {
      name: providerData.name,
      category: providerData.category,
      phoneNumber: providerData.phoneNumber,
      email: providerData.email,
      address: providerData.address,
      location: providerData.location?.trim() || '',
      description: providerData.description,
      businessHours: providerData.businessHours,
      isPriorityListing: providerData.isPriorityListing || false,
      lastUpdated: serverTimestamp(),
      updatedBy: user.uid,
      userId: providerData.userId
    });

  } catch (error) {
    console.error('Error in updateProvider:', error);
    throw new Error(`Failed to update provider: ${error.message}`);
  }
};

export const deleteProvider = async (providerId) => {
  try {
    // Get the current user
    const user = auth.currentUser;
    if (!user) {
      throw new Error('You must be logged in to delete a provider');
    }

    // Debug logging
    console.log('Current user attempting delete:', user.email);

    // Check if user has admin email
    if (user.email !== 'admin@aroundu.co.za') {
      throw new Error('Only admin users can delete providers');
    }

    // Get reference to the provider document
    const providerRef = doc(db, 'providers', providerId);

    // Delete the provider
    await deleteDoc(providerRef);
    
    console.log('Provider deleted successfully:', providerId);
    return true;
  } catch (error) {
    console.error('Detailed error in deleteProvider:', error);
    throw new Error('Failed to delete provider: ' + error.message);
  }
};

export const addReview = async (providerId, review) => {
  try {
    const providerRef = doc(db, 'serviceProviders', providerId);
    const providerDoc = await getDoc(providerRef);
    const provider = providerDoc.data();
    
    const reviews = [...provider.reviews, review];
    const rating = reviews.reduce((acc, curr) => acc + curr.rating, 0) / reviews.length;
    
    await updateDoc(providerRef, {
      reviews,
      rating
    });
  } catch (error) {
    throw new Error('Failed to add review: ' + error.message);
  }
};

// Add a new category
export const addCategory = async (categoryName) => {
  try {
    // Check if category already exists
    const categoryQuery = query(
      collection(db, 'categories'),
      where('name', '==', categoryName)
    );
    const existingCategories = await getDocs(categoryQuery);
    
    if (!existingCategories.empty) {
      throw new Error('Category already exists');
    }

    // Add new category
    const docRef = await addDoc(collection(db, 'categories'), {
      name: categoryName,
      createdAt: new Date().toISOString()
    });
    
    return {
      id: docRef.id,
      name: categoryName
    };
  } catch (error) {
    throw new Error('Failed to add category: ' + error.message);
  }
};

// Get all categories
export const getAllCategories = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'categories'));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    throw new Error('Failed to get categories: ' + error.message);
  }
};

// Add this function to firebase.js
export const ingestContractorsData = async (contractors) => {
  try {
    // First sign in as admin
    await signInWithEmailAndPassword(
      auth, 
      process.env.REACT_APP_ADMIN_EMAIL, 
      process.env.REACT_APP_ADMIN_PASSWORD
    );

    const batch = [];
    let successCount = 0;
    
    for (const contractor of contractors) {
      // Skip empty rows
      if (!contractor.Name || !contractor.Category) continue;

      // Format phone numbers
      let phoneNumber = contractor.Number || 'N/A';
      let phoneNumber2 = null;
      
      if (phoneNumber.includes('\n')) {
        [phoneNumber, phoneNumber2] = phoneNumber.split('\n').map(n => n.trim());
      }

      const providerData = {
        id: `provider_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: contractor.Name.trim(),
        category: contractor.Category.trim(),
        phoneNumber: phoneNumber,
        phoneNumber2: phoneNumber2,
        email: contractor.Email?.trim() || 'N/A',
        comment: contractor.Comment?.trim() || '',
        rating: 0,
        reviews: [],
        verified: true,
        createdAt: new Date().toISOString()
      };

      // Use setDoc with a specific ID instead of addDoc
      const providerRef = doc(db, 'providers', providerData.id);
      batch.push(setDoc(providerRef, providerData));
      successCount++;

      // Process in smaller batches to avoid overwhelming Firestore
      if (batch.length >= 100) {
        await Promise.all(batch);
        batch.length = 0;
      }
    }

    // Process any remaining items
    if (batch.length > 0) {
      await Promise.all(batch);
    }

    return `Successfully ingested ${successCount} providers`;
  } catch (error) {
    console.error('Error ingesting contractors:', error);
    throw new Error('Failed to ingest contractors: ' + error.message);
  }
};

// Function to clean up duplicate providers
export const cleanupDuplicateProviders = async () => {
  try {
    console.log('Starting duplicate cleanup process');
    
    const providersSnapshot = await getDocs(collection(db, 'providers'));
    const providers = providersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Create a map to store unique providers by name and category
    const uniqueProviders = new Map();
    const duplicates = [];

    providers.forEach(provider => {
      const key = `${provider.name.toLowerCase()}-${provider.category.toLowerCase()}`;
      if (!uniqueProviders.has(key)) {
        uniqueProviders.set(key, provider);
      } else {
        duplicates.push(provider);
      }
    });

    console.log(`Found ${duplicates.length} duplicates`);

    // Delete duplicate providers
    const batch = [];
    for (const duplicate of duplicates) {
      const providerRef = doc(db, 'providers', duplicate.id);
      batch.push(deleteDoc(providerRef));
    }

    if (batch.length > 0) {
      await Promise.all(batch);
      console.log('Successfully deleted duplicates');
    }

    return `Successfully removed ${duplicates.length} duplicate providers`;
  } catch (error) {
    console.error('Error cleaning up duplicates:', error);
    throw error;
  }
};

// Add this new function
export const initializeProviders = async () => {
  try {
    const batch = [];
    serviceProvidersData.forEach(provider => {
      const providerRef = doc(db, 'providers', provider.id);
      batch.push(setDoc(providerRef, provider));
    });
    
    await Promise.all(batch);
    console.log('Providers initialized successfully');
  } catch (error) {
    console.error('Error initializing providers:', error);
  }
};

// Add this new function to test authentication
export const testAdminAuth = async () => {
  try {
    const result = await signInWithEmailAndPassword(
      auth,
      'admin@aroundu.co.za',
      '@dmin@2025'
    );
    console.log('Authentication successful:', result.user);
    return true;
  } catch (error) {
    console.error('Authentication error:', error);
    throw error;
  }
};

// Modify the ingestJSONData function
export const ingestJSONData = async (jsonData) => {
  try {
    // First verify we can authenticate
    await testAdminAuth();
    console.log('Admin authentication successful');

    const batch = [];
    let successCount = 0;
    
    for (const contractor of jsonData) {
      // Skip empty rows
      if (!contractor.Name || !contractor.Category) continue;

      // Format phone numbers - ensure it's a string and handle null/undefined
      let phoneNumber = contractor.Number ? String(contractor.Number).trim() : 'N/A';
      let phoneNumber2 = null;
      
      // Check if the phone number contains a newline
      if (phoneNumber && phoneNumber.includes('\n')) {
        const numbers = phoneNumber.split('\n').map(n => n.trim());
        phoneNumber = numbers[0];
        phoneNumber2 = numbers[1];
      }

      const providerData = {
        id: `provider_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: contractor.Name.trim(),
        category: contractor.Category.trim(),
        phoneNumber: phoneNumber,
        phoneNumber2: phoneNumber2,
        email: contractor.Email?.trim() || 'N/A',
        comment: contractor.Comment?.trim() || '',
        rating: 0,
        reviews: [],
        verified: true,
        createdAt: new Date().toISOString()
      };

      // Use setDoc with a specific ID instead of addDoc
      const providerRef = doc(db, 'providers', providerData.id);
      batch.push(setDoc(providerRef, providerData));
      successCount++;

      // Process in smaller batches to avoid overwhelming Firestore
      if (batch.length >= 100) {
        await Promise.all(batch);
        batch.length = 0;
      }
    }

    // Process any remaining items
    if (batch.length > 0) {
      await Promise.all(batch);
    }

    return `Successfully ingested ${successCount} providers`;
  } catch (error) {
    console.error('Error ingesting JSON data:', error);
    throw new Error('Failed to ingest JSON data: ' + error.message);
  }
}; 